import config from '../config';
import React, { useState, useEffect } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";

import {
  Settings,
  Briefcase,
  Plane,
  ChevronDown,
  ChevronUp,
} from "lucide-react";
import "../style/organizationDetail.css";
import { useAuth } from "../context/authContext";
import Button from "../components/Button";

const OrganizationDetail = () => {
  const { authData, userData } = useAuth(); // Added userData
  const { organizationId } = useParams();
  const navigate = useNavigate();

  const [orgName, setOrgName] = useState("");
  const [accounts, setAccounts] = useState([]);
  const [locations, setLocations] = useState({});
  const [campaigns, setCampaigns] = useState({});
  const [expandedAccounts, setExpandedAccounts] = useState({});
  const [expandedSections, setExpandedSections] = useState({});
  const location = useLocation();

  const [loading, setLoading] = useState({
    organization: true,
    accounts: true,
  });
  const [error, setError] = useState({
    organization: null,
    accounts: null,
  });

  // Check if initial data is loaded
  const isLoading = loading.organization || loading.accounts;

  // Check if user is ADMIN
  const isAdmin = userData?.role === "ADMIN";

  // Fetch organization details
  useEffect(() => {
    const fetchOrganizationDetails = async () => {
      try {
        setLoading((prev) => ({ ...prev, organization: true }));
        setError((prev) => ({ ...prev, organization: null }));

        const response = await fetch(
          `${config.BASE_URL}/organizations/${organizationId}`,
          {
            method: "GET",
            headers: {
              Authorization: `Bearer ${authData.accessToken}`,
              "Content-Type": "application/json",
            },
          }
        );

        if (!response.ok) {
          throw new Error("Failed to fetch organization details");
        }

        const data = await response.json();
        if (data && data.name) {
          setOrgName(data.name);
        }
      } catch (error) {
        console.error("Error fetching organization details:", error);
        setError((prev) => ({ ...prev, organization: error.message }));
      } finally {
        setLoading((prev) => ({ ...prev, organization: false }));
      }
    };

    if (organizationId) {
      fetchOrganizationDetails();
    }
  }, [organizationId, authData.accessToken]);

  // Fetch accounts for the organization
  useEffect(() => {
    const fetchAccounts = async () => {
      try {
        setLoading((prev) => ({ ...prev, accounts: true }));
        setError((prev) => ({ ...prev, accounts: null }));

        const response = await fetch(
          `${config.BASE_URL}/accounts/org/${organizationId}`,
          {
            method: "GET",
            headers: {
              Authorization: `Bearer ${authData.accessToken}`,
              "Content-Type": "application/json",
            },
          }
        );

        if (!response.ok) {
          throw new Error("Failed to fetch accounts");
        }

        const data = await response.json();
        const sortedAccounts = Array.isArray(data)
          ? data.sort((a, b) => a.name.localeCompare(b.name))
          : [data];
        setAccounts(sortedAccounts);

       // Expand the account passed via navigation (e.g., after creating a campaign/location)
const expandedIdFromNav = location.state?.expandedAccountId;

if (sortedAccounts.length > 0) {
  const defaultExpandedId = expandedIdFromNav || sortedAccounts[0].id;

  setExpandedAccounts((prev) => ({
    ...prev,
    [defaultExpandedId]: true,
  }));
}

      } catch (error) {
        console.error("Error fetching accounts:", error);
        setError((prev) => ({ ...prev, accounts: error.message }));
        setAccounts([]);
      } finally {
        setLoading((prev) => ({ ...prev, accounts: false }));
      }
    };

    if (organizationId) {
      fetchAccounts();
    }
  }, [organizationId, authData.accessToken]);

  // Fetch locations and campaigns when an account is expanded
  useEffect(() => {
    const fetchAccountDetails = async (accountId) => {
      // Skip if data already exists
      if (
        locations[accountId] !== undefined &&
        campaigns[accountId] !== undefined
      ) {
        return;
      }

      try {
        // Set loading state for this account
        setLoading((prev) => ({
          ...prev,
          [`locations_${accountId}`]: true,
          [`campaigns_${accountId}`]: true,
        }));

        // Fetch locations
        const locationsResponse = await fetch(
          `${config.BASE_URL}/locations/location/${accountId}`,
          {
            method: "GET",
            headers: {
              Authorization: `Bearer ${authData.accessToken}`,
              "Content-Type": "application/json",
            },
          }
        );

        const locationsData = locationsResponse.ok
          ? await locationsResponse.json()
          : [];

        // Fetch campaigns
        const campaignsResponse = await fetch(
          `${config.BASE_URL}/campaigns/campaign/${accountId}`,
          {
            method: "GET",
            headers: {
              Authorization: `Bearer ${authData.accessToken}`,
              "Content-Type": "application/json",
            },
          }
        );

        const campaignsData = campaignsResponse.ok
          ? await campaignsResponse.json()
          : [];

        // Update state with new data
        setLocations((prev) => ({
          ...prev,
          [accountId]: locationsData,
        }));

        setCampaigns((prev) => ({
          ...prev,
          [accountId]: campaignsData,
        }));
      } catch (error) {
        console.error(
          `Error fetching details for account ${accountId}:`,
          error
        );
      } finally {
        setLoading((prev) => ({
          ...prev,
          [`locations_${accountId}`]: false,
          [`campaigns_${accountId}`]: false,
        }));
      }
    };

    // Find which accounts are newly expanded
    const newlyExpandedAccounts = accounts.filter(
      (account) =>
        expandedAccounts[account.id] &&
        (locations[account.id] === undefined ||
          campaigns[account.id] === undefined)
    );

    // Fetch data for newly expanded accounts
    newlyExpandedAccounts.forEach((account) => {
      fetchAccountDetails(account.id);
    });
  }, [expandedAccounts, accounts, authData.accessToken, locations, campaigns]);

  // Toggle account expansion
  const toggleAccountExpansion = (accountId) => {
    setExpandedAccounts((prev) => ({
      ...prev,
      [accountId]: !prev[accountId],
    }));
  };

  // Toggle section expansion
  const toggleSectionExpansion = (accountId, section) => {
    setExpandedSections((prev) => ({
      ...prev,
      [accountId]: {
        ...prev[accountId],
        [section]: !prev[accountId]?.[section],
      },
    }));
  };

  // Handle account click
  const handleAccountClick = (accountId, e) => {
    e.stopPropagation();
    navigate(`/organization/${organizationId}/account/${accountId}/edit`, {
      state: {
        orgId: organizationId,
        orgName: "Organization Name",
        editMode: true,
        accountId,
      },
    });
  };

  // Handle back button click
  const handleBackClick = () => {
    navigate('/organizations')
  };

  return (
    <div className="organizationDetail-page">
      <div className="organizationDetail-content-wrapper">
        {/* Only show back button if user is not ADMIN */}
        {!isAdmin && (
          <div className="organizationDetail-header1">
            <Button
              className="organizationDetail-backButton"
              onClick={handleBackClick}
              text="← Back"
              color="#006d7d"
            />
          </div>
        )}

        {isLoading ? (
          <div className="organizationDetail-loadingContainer">
            <div className="organizationDetail-loader"></div>
            <p>Loading organization data...</p>
          </div>
        ) : error.organization || error.accounts ? (
          <div className="organizationDetail-errorContainer">
            {error.organization || error.accounts}
          </div>
        ) : (
          <div className="organizationDetail-container">
            <div className="organizationDetail-header">
              <h1 className="organizationDetail-title">
                {orgName || "Organization"}
              </h1>
              <Button
                className="organizationDetail-addAccountButton"
                onClick={() => {
                  navigate(`/organization/${organizationId}/account/add`, {
                    state: { orgId: organizationId, orgName: orgName },
                  });
                }}
                text="Add Account"
                color="#006d7d"
              />
            </div>

            {loading.accounts ? (
              <div className="organizationDetail-sectionLoading">
                <div className="organizationDetail-miniLoader"></div>
                <p>Loading accounts...</p>
              </div>
            ) : error.accounts ? (
              <div className="organizationDetail-sectionError">
                Error loading accounts: {error.accounts}
              </div>
            ) : (
              <div className="organizationDetail-accountList">
                {accounts.length > 0 ? (
                  accounts.map((account) => (
                    <div
                      key={account.id}
                      className="organizationDetail-accountContainer"
                    >
                      <div
                        className="organizationDetail-accountHeader"
                        onClick={() => toggleAccountExpansion(account.id)}
                      >
                        <div className="organizationDetail-accountTitleWrapper">
                          {expandedAccounts[account.id] ? (
                            <ChevronUp className="organizationDetail-expanderIcon" />
                          ) : (
                            <ChevronDown className="organizationDetail-expanderIcon" />
                          )}
                          <h2 className="organizationDetail-accountTitle">
                            {account.name}
                          </h2>
                        </div>
                        <Settings
                          onClick={(e) => handleAccountClick(account.id, e)}
                          className="organizationDetail-settingsIcon"
                        />
                      </div>

                      {expandedAccounts[account.id] && (
                        <div className="organizationDetail-accountContent">
                          {loading[`campaigns_${account.id}`] ||
                            loading[`locations_${account.id}`] ? (
                            <div className="organizationDetail-sectionLoading">
                              <div className="organizationDetail-miniLoader"></div>
                              <p>Loading account details...</p>
                            </div>
                          ) : (
                            <>
                              <AccountSection
                                title="Campaigns"
                                items={campaigns[account.id] || []}
                                addButtonText="Add Campaign"
                                isExpanded={
                                  expandedSections[account.id]?.campaigns
                                }
                                onToggle={() =>
                                  toggleSectionExpansion(
                                    account.id,
                                    "campaigns"
                                  )
                                }
                                accountId={account.id}
                                organizationId={organizationId}
                                loading={loading[`campaigns_${account.id}`]}
                                error={error.campaigns}
                              />
                              <AccountSection
                                title="Locations"
                                items={locations[account.id] || []}
                                addButtonText="Add Location"
                                isExpanded={
                                  expandedSections[account.id]?.locations
                                }
                                onToggle={() =>
                                  toggleSectionExpansion(
                                    account.id,
                                    "locations"
                                  )
                                }
                                accountId={account.id}
                                organizationId={organizationId}
                                loading={loading[`locations_${account.id}`]}
                                error={error.locations}
                              />
                            </>
                          )}
                        </div>
                      )}
                    </div>
                  ))
                ) : (
                  <div className="organizationDetail-noAccounts">
                    No accounts found for this organization.
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

const AccountSection = ({
  title,
  items,
  addButtonText,
  isExpanded,
  onToggle,
  accountId,
  organizationId,
  loading,
  error,
}) => {
  const [sortOrder, setSortOrder] = useState("A-Z");
  const [searchQuery, setSearchQuery] = useState("");
  const [isActiveOnly, setIsActiveOnly] = useState(false);
  const navigate = useNavigate();

  // Handle add button click
  const handleAddClick = () => {
    if (title === "Campaigns") {
      navigate(`/organization/${organizationId}/campaigns/add`, {
        state: {
          accountId: accountId,
          organizationId: organizationId,
          orgId: organizationId
        },
      });
    } else if (title === "Locations") {
     navigate(`/organization/${organizationId}/locations/add`, {
  state: { accountId, organizationId },
});

    }
  };

  // Handle item click
  const handleItemClick = (item) => {
    if (title === "Campaigns") {
      navigate(`/organization/${organizationId}/campaigns/${item.id}`, {
        state: {
          item,
          editMode: true,
          accountId: accountId,
          organizationId: organizationId,
        },
      });
    } else if (title === "Locations") {
      navigate(`/organization/${organizationId}/locations/${item.id}`, {
        state: {
          item,
          editMode: true,
          accountId: accountId,
          organizationId: organizationId,
        },
      });
    }
  };

  // Filter and sort items
  const filteredItems = Array.isArray(items)
    ? items
      .filter((item) =>
        item.name?.toLowerCase().includes(searchQuery.toLowerCase())
      )
      .filter((item) => (isActiveOnly ? item.active : true))
      .sort((a, b) => {
        if (sortOrder === "A-Z") {
          return a.name.localeCompare(b.name);
        } else if (sortOrder === "Z-A") {
          return b.name.localeCompare(a.name);
        }
        return 0;
      })
    : [];

  const itemsToShow = isExpanded ? filteredItems : filteredItems.slice(0, 5);

  return (
    <div className="organizationDetail-section">
      <h3 className="organizationDetail-sectionTitle">{title}</h3>
      <div className="organizationDetail-searchContainer">
        <input
          type="text"
          placeholder={`Search for ${title.toLowerCase()}...`}
          className="organizationDetail-searchInput"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
        />
        <Button
          className="organizationDetail-clearButton"
          color={searchQuery ? "#006d7d" : "gray"}
          text="Clear"
          onClick={() => setSearchQuery("")}
        />
        <Button
          className="organizationDetail-addButton"
          onClick={handleAddClick}
          text={addButtonText}
          color="#006d7d"
        />
      </div>
      <div className="organizationDetail-sortFilterContainer">
        <div className="organizationDetail-sort">
          <span className="organizationDetail-sortLabel">Sort By:</span>
          <select
            className="organizationDetail-sortSelect"
            value={sortOrder}
            onChange={(e) => setSortOrder(e.target.value)}
          >
            <option value="A-Z">A → Z</option>
            <option value="Z-A">Z → A</option>
          </select>
        </div>
        <div className="organizationDetail-toggleContainer">
          <span className="organizationDetail-toggleLabel">Show All</span>
          <label className="organizationDetail-toggleSwitch">
            <input
              type="checkbox"
              checked={isActiveOnly}
              onChange={() => setIsActiveOnly((prev) => !prev)}
            />
            <span className="organizationDetail-toggleSlider"></span>
          </label>
          <span className="organizationDetail-toggleLabel">Active Only</span>
        </div>
      </div>

      {loading ? (
        <div className="organizationDetail-sectionLoading">
          <div className="organizationDetail-miniLoader"></div>
          <p>Loading {title.toLowerCase()}...</p>
        </div>
      ) : error ? (
        <div className="organizationDetail-sectionError">
          Error loading {title.toLowerCase()}: {error}
        </div>
      ) : (
        <>
          <ul className="organizationDetail-listContainer">
            {itemsToShow.length > 0 ? (
              itemsToShow.map((item, index) => (
                <li
                  key={index}
                  className="organizationDetail-listItem"
                  onClick={() => handleItemClick(item)}
                >
                  {title === "Campaigns" ? (
                    <>
                      <Briefcase
                        size={35}
                        className="organizationDetail-icon"
                      />
                      <div className="organizationDetail-itemText">
                        <div className="organizationDetail-itemMain">
                          {item.name}
                        </div>
                        <div className="organizationDetail-itemSub">:</div>
                        <div className="organizationDetail-itemSub">
                          {item.Account?.name || ""}
                        </div>
                      </div>
                    </>
                  ) : (
                    <>
                      <Plane size={35} className="organizationDetail-icon" />
                      <div className="organizationDetail-itemText">
                        <div className="organizationDetail-itemMain">
                          {item.name}
                        </div>
                        <div className="organizationDetail-itemSub">
                          {item.country}
                        </div>
                      </div>
                    </>
                  )}
                </li>
              ))
            ) : (
              <li className="organizationDetail-noItems">
                No {title.toLowerCase()} found
              </li>
            )}
          </ul>
          {filteredItems.length > 5 && (
            <Button
              className="organizationDetail-showMoreButton"
              onClick={onToggle}
              text={isExpanded ? "^ Show Less" : "v Show More"}
              color="#006d7d"
            />
          )}
        </>
      )}
    </div>
  );
};

export default OrganizationDetail;
